<template>
    <div class="domain-settings">
        <section>
            <h4>{{ $options.MESSAGE_CONSTANTS.setupCustomDomainTitle }}</h4>

            <p class="description">
                {{ $options.MESSAGE_CONSTANTS.customDomainDescription }}
            </p>

            <ds-filled-button
                data-qa="add-custom-domain-button-qa"
                class="domain-add-button"
                :disabled="!isEmptyState"
                @click="addDomain"
            >
                {{ $options.MESSAGE_CONSTANTS.addDomainBtn }}
            </ds-filled-button>

            <domain-authentication-modal
                v-if="isDomainAuthenticationModalOpen"
                :is-open="isDomainAuthenticationModalOpen"
                :is-new-domain="isNewDomain"
                :is-saving="isSaving"
                :domain="retryInfo"
                :current-env="currentEnv"
                @submit-form="submitForm"
                @close="close"
                @copy="copy"
            />
        </section>

        <domain-list
            v-if="!isEmptyState"
            :domain="customDomain"
            @remove="removeCustomDomain"
            @retry="retryCustomDomain"
            @check="checkCustomDomain"
        />
    </div>
</template>

<script>

import DomainList from './DomainList.vue';
import DomainAuthenticationModal from './DomainAuthenticationModal.vue';
import {
    deleteCustomDomain,
    deleteFormsApiCustomDomain,
    getCustomDomainStatus,
    getCustomDomainsForTenant,
    createCustomDomain,
    createFormsApiCustomDomain,
} from '../util/marketing/marketingSiteEditorApi';

export const MESSAGE_CONSTANTS = {
    setupCustomDomainTitle: 'Set up Custom Domain',
    customDomainDescription: "If you would like contacts to reach your landing pages through your existing business domain, click the \"Add a domain\" button below and we'll walk you through a step-by-step process to set up the needed records with your domain provider.",
    addDomainBtn: 'Add a domain',
    successHeadline: 'Text copied',
    domainStatusUpdated: 'Domain status updated',
    errorGettingStatus: 'Error getting the status',
    errorRetryMessage: 'An error occurred with the custom domain authentication setup, please try again.',
    errorRemovingMessage: 'Error removing your domain, please try again',
    errorCreatingMessage: 'Create Custom Domain Error',
};

export default {
    MESSAGE_CONSTANTS,
    components: {
        DomainAuthenticationModal,
        DomainList,
    },
    props: {
        tenantId: {
            type: String,
            required: true,
        },
        userId: {
            type: Number,
            default: 0,
        },
        currentEnv: {
            type: String,
        },
    },
    data() {
        return {
            isDomainAuthenticationModalOpen: false,
            isNewDomain: true,
            invalidNewDomain: false,
            error: '',
            isCustomDomainValidated: false,
            isSaving: false,
            customDomain: {
                type: Object,
            },
            retryInfo: {
                type: Object,
                default: () => {},
            },
        };
    },
    created() {
        this.load();
    },
    computed: {
        isEmptyState() {
            return this.customDomain.domainName ? this.customDomain.domainName === '' : true;
        },
    },
    methods: {
        close() {
            this.isDomainAuthenticationModalOpen = false;
        },
        copy(value) {
            const successHeadLine = MESSAGE_CONSTANTS.successHeadline;
            const successMessage = `${value} has been copied to your clipboard.`;
            const errorMessage = `Error copying text ${value}`;

            return this.$copyText(value).then(() => {
                this.$toast({
                    headline: successHeadLine,
                    message: successMessage,
                });
            }, () => {
                this.$error({
                    message: errorMessage,
                });
            });
        },
        async submitForm(dataToSend) {
            this.isSaving = true;

            try {
                const createCustomDomainCommand = {
                    tenantId: this.tenantId,
                    domainName: dataToSend.domain,
                    setupComplete: true,
                };

                const createFormsApiCustomDomainCommand = {
                    domain: dataToSend.domain,
                    createdById: this.userId,
                };

                const { data } = await createCustomDomain(createCustomDomainCommand);

                this.customDomain = { ...data, setupComplete: false };

                await createFormsApiCustomDomain(createFormsApiCustomDomainCommand);
                await this.getStatusCustomDomain();
                this.close();
            } catch (error) {
                this.$error({ message: MESSAGE_CONSTANTS.errorCreatingMessage });
            }

            this.isSaving = false;
        },
        async load() {
            try {
                this.customDomain = await getCustomDomainsForTenant();
                await this.getStatusCustomDomain();
            } catch (error) {
                this.customDomain = {};
                this.$error({ message: MESSAGE_CONSTANTS.errorGettingStatus });
            }
        },
        async getStatusCustomDomain(showMessage = false) {
            if (this.customDomain?.domainName) {
                try {
                    this.isCustomDomainValidated = await getCustomDomainStatus(this.customDomain.domainName);
                    this.customDomain = { ...this.customDomain, setupComplete: this.isCustomDomainValidated };

                    if (showMessage) {
                        this.$toast({ message: MESSAGE_CONSTANTS.domainStatusUpdated });
                    }
                } catch (error) {
                    this.$error({ message: MESSAGE_CONSTANTS.errorGettingStatus });
                }
            }
        },
        checkCustomDomain(domain) {
            this.customDomain = { ...this.customDomain, domainName: domain.domainName };
            this.getStatusCustomDomain(true);
        },
        async retryCustomDomain(domain) {
            try {
                this.retryInfo = {
                    domainName: domain.domainName,
                };
                await deleteFormsApiCustomDomain(domain.domainName, this.userId);
                await deleteCustomDomain(domain.id);
                this.isDomainAuthenticationModalOpen = true;
                this.isNewDomain = false;
                this.$toast({ message: MESSAGE_CONSTANTS.domainStatusUpdated });
            } catch (error) {
                this.$error({ message: MESSAGE_CONSTANTS.errorRetryMessage });
            }
        },
        async removeCustomDomain(domain) {
            try {
                await deleteCustomDomain(domain.id);
                await deleteFormsApiCustomDomain(domain.domainName, this.userId);
                this.customDomain = {};
                this.isNewDomain = true;
            } catch (error) {
                this.$error({ message: MESSAGE_CONSTANTS.errorRemovingMessage });
            }
        },
        addDomain() {
            this.isNewDomain = true;
            this.retryInfo = {};
            this.isDomainAuthenticationModalOpen = true;
        },
    },
};

</script>

<style lang="scss" rel="stylesheet/scss" type="text/scss" scoped>
.domain-settings {
    @include container;

    > section {
        max-width: px-to-rem(600px);
    }
}

button.button.domain-add-button {
    color: white;
}

.domain-add-button {
    margin-bottom: 20px;
}

h4 {
    font-weight: bold;
    margin-bottom: 20px;
}

</style>
